import { GetSingleDeviceGroupResponse } from "src/api/generated/frontend_internal_v1";
import { UUID } from "src/api/api_common";
import { ApiInternal } from "src/api/api_internal";
import React, { useEffect } from "react";

/** Data provided by the {@link SINGLE_DEVICE_GROUP_CONTEXT} */
export type SingleDeviceGroupContext = {
    /** The currently available device group */
    deviceGroup: GetSingleDeviceGroupResponse;

    /** Reload the device groups' information */
    reset: () => void;
};

/** {@link React.Context} to access the device groups */
const SINGLE_DEVICE_GROUP_CONTEXT = React.createContext<SingleDeviceGroupContext>({
    deviceGroup: {
        name: "",
        uuid: "",
        description: "",
        devices: [],
        access_rules: { outbound: [], inbound: [] },
        rulesets: { outbound: [], inbound: [] },
    },

    /**
     * Reset the device groups
     */
    reset: () => {},
});
SINGLE_DEVICE_GROUP_CONTEXT.displayName = "SingleDeviceGroupContext";
export default SINGLE_DEVICE_GROUP_CONTEXT;

/**
 * The properties for {@link SingleDeviceGroupProvider}
 */
export type SingleDeviceGroupProviderProps = {
    /** Identifier for the device group */
    uuid: UUID;

    /**
     * Children of the provider
     */
    children: React.ReactNode;
};

/**
 * The provider of a device group context
 */
export function SingleDeviceGroupProvider(props: SingleDeviceGroupProviderProps) {
    const [deviceGroup, setDeviceGroup] = React.useState<GetSingleDeviceGroupResponse | "loading">("loading");
    let fetching = false;

    /**
     * Fetch the device groups
     */
    const fetchDeviceGroup = async () => {
        if (fetching) return;
        fetching = true;

        const res = await ApiInternal.deviceGroups.getSingle(props.uuid);
        setDeviceGroup(res);

        fetching = false;
    };

    useEffect(() => {
        fetchDeviceGroup().then();
    }, [props.uuid]);

    if (deviceGroup === "loading") {
        return <div>Loading ..</div>;
    }

    return (
        <SINGLE_DEVICE_GROUP_CONTEXT.Provider
            value={{
                deviceGroup: deviceGroup,
                reset: fetchDeviceGroup,
            }}
        >
            {props.children}
        </SINGLE_DEVICE_GROUP_CONTEXT.Provider>
    );
}
